import type { FilterGroup } from 'src/utils/common'
import React, { useEffect, useRef, useContext } from 'react'
import { reaction, toJS } from 'mobx'
import { observer, useLocalObservable } from 'mobx-react'
import { TreePageTree, TreePageMain, TreePageOrg } from './components'
import { appStore } from 'src/stores'
import { api, $api, $consts, $evt } from 'src/plugins'
import { useDomSize, useLatest } from 'src/hooks'
import { getMenuFormParams, getValidBtns, checkCondition } from './helper'
import { initFilter, extendRulesAndGroups } from 'src/utils/common'
import { omit, isEqual } from 'lodash'
import 'src/styles/components/root/treePage.less'

type FormData = {
  // 当前表单父表单配置的 formId（比如说树表单，组合表单）
  parentFormId?: string,
  // 查询参数
  filterGroup?: FilterGroup,
  // 额外需要保存参数
  extraSaveData?: { [field: string]: any }
}

type Props = {
  formCode: string,
  formData?: FormData,
}

const TreePropsContext = React.createContext<Props>({ formCode: '' })
const TreeContext = React.createContext<any>(undefined)

const CheckTreePage = observer(() => {
  const tree = useContext(TreeContext)

  if (tree.treeConfig.Nav_PageTree?.SHOW_MODEL === undefined) return null

  if (tree.treeConfig.Nav_PageTree?.SHOW_MODEL === $consts['TABLE/TREE_TYPE_HORIZONTAL']) return <TreePageOrg />

  return (
    <React.Fragment>
      <TreePageTree />
      <TreePageMain />
    </React.Fragment>
  )
})

const TreePage = (props: Props) => {
  const treeProps = useLatest(props)

  const state = useLocalObservable(() => {
    return {
      // 树表单配置
      treeConfig: {} as any,
      setTreeConfig (config: any) {
        this.treeConfig = config
      },
      getTreeConfig (formCode: string) {
        const { OrgId } = appStore.current.headers
        const payload = initFilter({ OrgId, Keyword: formCode })
        $api['tree/getTreePageConfigInfo'](payload)
          .then((res: any) => {
            res && this.setTreeConfig(res)
            return res
          })
          .then((res: any) => {
            res && this.getTreeData()
          })
      },
      // 树数据
      treeData: [] as any,
      setTreeData (data: any[]) {
        this.treeData = data
      },
      getTreeData () {
        const { OrgId } = appStore.current.headers
        const { Nav_PageTree = {} } = this.treeConfig
        const {
          QUERY_API_URL,
          SORT: Sort,
          ORDER: Order,
          FILTER_ORG_TYPE: OrgType,
          IGNORE_ORG_RULE: IgnoreOrgRule
        } = Nav_PageTree
        const payload: any = initFilter({ OrgId, Sort, Order, OrgType, IgnoreOrgRule })
        // 一次性加载全部数据
        payload.Level = -1
        // 菜单参数
        payload.MenuParameter = appStore.current.activeMenu.MENU_FORM_PARAMS
        // 表单参数
        const formParams = getMenuFormParams(appStore.current.activeMenu)
        for (const [key, value] of Object.entries(formParams)) {
          payload[key] = value
        }
        // 额外请求参数
        treeProps.current.formData?.filterGroup && extendRulesAndGroups(payload, treeProps.current.formData?.filterGroup)

        $api['app/getDataByPost'](payload, {
          url: QUERY_API_URL
        }).then((data: any) => {
          data && this.setTreeData(data)
        })
      },
      // 拍平后一维树数据
      get flatTreeData () {
        const flatData = (tree: any[] = [], data: any[] = []) => {
          if (Array.isArray(tree) && tree.length) {
            tree.forEach(item => {
              data.push(omit(item, ['Children']))
              flatData(item.Children, data)
            })
          }
        }
        const targetData: any[] = []
        flatData(toJS(this.treeData), targetData)
        return targetData
      },
      // 关键字搜索
      searchKey: '',
      setSearchKey (key: string) {
        this.searchKey = key
      },
      // 搜索过滤后的树数据
      get visibleTreeData () {
        if (!this.searchKey) return this.treeData
        return this.flatTreeData.filter(({ Node }) => Node.NAME?.indexOf(this.searchKey) !== -1)
      },
      // 树配置按钮
      get enableBtns () {
        return getValidBtns(this.treeConfig.Nav_Btns)
      },
      /**
       * 根据选中的节点获取节点配置的按钮
       * @param btns 所配置的按钮
       * @param selectedNodes 选中的节点
       * @returns 
       */
      getShowBtns (btns: any[], selectedNodes: any[]) {
        // 如果配置了树多选，那么直接显示所配置的所有页面
        const checkMultiShowBtn = (condition: string) => {
          const { MENU_FORM_PARAMS } = appStore.current.activeMenu

          let isShow = !condition || !MENU_FORM_PARAMS
          if (condition && MENU_FORM_PARAMS) {
            const conditionsOr = condition.split('|')
            const conditionsAnd = condition.split('&')
            if (conditionsOr.length === 1 && conditionsAnd.length === 1) {
              // 只配置了一个条件
              if (condition === MENU_FORM_PARAMS) {
                isShow = true
              }
            } else if (conditionsOr.length > 1) {
              // 或条件
              for (let conditionOr of conditionsOr) {
                if (conditionOr === MENU_FORM_PARAMS) {
                  isShow = true
                  break
                }
              }
            } else if (conditionsAnd.length > 1) {
              // 与条件
              const showResults = []
              for (let conditionAnd of conditionsAnd) {
                if (conditionAnd === MENU_FORM_PARAMS) {
                  showResults.push(true)
                }
              }
              isShow = showResults.length === conditionsAnd.length
            }
          }

          return isShow
        }

        const showBtns: any[] = []
        btns.forEach((btn: any) => {
          const selectData = selectedNodes[0]
          // 如果配置了树多选，那么直接显示所配置的所有页面
          const isShow = selectedNodes.length > 1 ? checkMultiShowBtn(btn.BTN_CONDITION) : checkCondition(btn.BTN_CONDITION, selectData, appStore.current.activeMenu.MENU_FORM_PARAMS)
          if (isShow) {
            // 重设按钮为新增同级的按钮
            if (btn.BTN_TYPE === $consts['TABLE/BTN_TYPE_ADD'] && btn.IS_SAME_LEVEL) {
              btn.BTN_TYPE = $consts['TABLE/BTN_TYPE_TREE_SAMELEVEL_ADD']
            }
            showBtns.push(btn)
          }
        })
        return showBtns
      },
      // 竖树默认展开项
      get defaultExpand () {
        const { Nav_PageTree = {} } = this.treeConfig
        const keys: string[] = []
        if (Nav_PageTree.LEVEL) {
          const checkTreeDataLevel = (data: any) => {
            data.forEach((item: any) => {
              if (item.Level < Nav_PageTree.LEVEL) {
                keys.push(item.Node.ID)
                if (Array.isArray(item.Children) && item.Children.length) {
                  checkTreeDataLevel(item.Children)
                }
              }
            })
          }
          checkTreeDataLevel(this.treeData)
        }
        return keys.length ? { defaultExpandedKeys: keys } : { defaultExpandAll: true }
      },
      // 竖树选中项
      selectedKeys: [] as string[],
      setSelectedKeys (keys: string[]) {
        this.selectedKeys = keys
      },
      // 竖树选中节点
      get selectedNodes () {
        return this.flatTreeData.filter(item => this.selectedKeys.indexOf(item.Node.ID) !== -1)
      },
      // 竖树表单页面按钮类型
      get pageBtnTypes () {
        const presetTypes = [
          $consts['TABLE/BTN_TYPE_CUSTOM_PAGE'], // 自定义表单按钮
          $consts['TABLE/BTN_TYPE_EDIT_PAGE'], // 编辑表单按钮
          $consts['TABLE/BTN_TYPE_LIST_PAGE'], // 列表表单按钮
          $consts['TABLE/BTN_TYPE_MULTI_PAGE'], // 组合表单按钮
        ]
        this.enableBtns.forEach((btn: any) => {
          // 行按钮，则按弹窗处理
          if (btn.BTN_FUN_TYPE === $consts['TABLE/BTN_FUN_TYPE_ROW']) {
            const index = presetTypes.indexOf(btn.BTN_TYPE)
            index !== -1 && presetTypes.splice(index, 1)
          }
        })
        return presetTypes
      },
      // 竖树选中节点所配置的以页面展示的按钮，显示在 Tree 右边
      get selectedNodesPageBtns () {
        if (!this.treeData.length) return []

        const pageBtns = toJS(this.enableBtns.filter((btn: any) => this.pageBtnTypes.indexOf(btn.BTN_TYPE) !== -1))

        if (!this.selectedNodes.length) return pageBtns

        return this.getShowBtns(pageBtns, this.selectedNodes)
      },
      // 竖树选中节点所配置的非页面展示的按钮，显示在 Tree 头部
      get selectedNodesToolBtns () {
        if (!this.treeData.length) return []

        const toolBtns = toJS(this.enableBtns.filter((btn: any) => this.pageBtnTypes.indexOf(btn.BTN_TYPE) === -1))

        if (!this.selectedNodes.length) return toolBtns

        return this.getShowBtns(toolBtns, this.selectedNodes)
      },
      // 页面宽高，主要用 Tree 设置虚拟滚动的高度
      pageDomSize: { clientWidth: 0, clientHeight: 0 },
      setPageDomSize (size: any) {
        Object.assign(this.pageDomSize, size)
      },
    }
  })

  // 树表单配置/数据
  useEffect(() => {
    state.getTreeConfig(props.formCode)
  }, [props.formCode])

  // 获取树默认选中项
  useEffect(() => reaction(
    () => {
      const { treeConfig, treeData } = state
      const { Nav_PageTree = {} } = treeConfig
      return {
        multiCheck: Nav_PageTree.MULTI_CHECK,
        multiCheckCount: Nav_PageTree.MULTI_CHECK_COUNT,
        treeData
      }
    },
    ({ multiCheck, multiCheckCount, treeData }) => {
      const defaultSelectedKeys: string[] = []
      if (Array.isArray(treeData) && treeData.length) {
        treeData.forEach(({ Node }) => {
          Node?.ID && defaultSelectedKeys.push(Node.ID)
        })
      }
      const newKeys = multiCheck ? (multiCheckCount ? defaultSelectedKeys.slice(0, multiCheckCount) : defaultSelectedKeys) : (defaultSelectedKeys.length ? [defaultSelectedKeys[0]] : [])
      const allKeys = state.flatTreeData.map(({ Node }) => Node?.ID)
      const copyKeys = toJS(state.selectedKeys)
      const currKeys = copyKeys.filter((key: string) => allKeys.indexOf(key) !== -1)
      // 无之前选中的节点时，重新设置选中项
      !currKeys.length && !isEqual(newKeys, copyKeys) && state.setSelectedKeys(newKeys)
    }
  ), [])

  // 附加树选中查询参数
  useEffect(() => {
    const dispose = reaction(
      () => state.selectedKeys,
      keys => api.setRequestParams({ commonPayload: { TreeSelected: keys.length ? keys : undefined } },)
    )

    // 父表单为组合表单时，切换 tab 时，更新树选中为当前 tab 所配置的树表单
    const handleTabChange = (tabKey: string) => {
      if (tabKey === props.formData?.parentFormId) {
        api.setRequestParams({ commonPayload: { TreeSelected: state.selectedKeys } })
      }
    }

    $evt.addListener($consts['EVT/COMBINATION_PAGE_TABCHANGE'], handleTabChange)

    return () => {
      dispose()
      $evt.removeListener($consts['EVT/COMBINATION_PAGE_TABCHANGE'], handleTabChange)
      api.unsetRequestParams('commonPayload.TreeSelected')
    }
  }, [])

  // 计算页面宽高
  const refOfTreePage = useRef(null)
  const domSize = useDomSize(refOfTreePage)
  useEffect(() => {
    const { clientWidth, clientHeight } = domSize
    clientWidth && clientHeight && state.setPageDomSize(domSize)
  }, [domSize])

  return (
    <TreePropsContext.Provider value={props}>
      <TreeContext.Provider value={state}>
        <div ref={refOfTreePage} className='tree-page'>
          <CheckTreePage />
        </div>
      </TreeContext.Provider>
    </TreePropsContext.Provider>
  )
}

export { TreePropsContext, TreeContext }
export default TreePage